import React, { useEffect, useState } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Clock, Play, Pause, RefreshCw, AlertCircle, CheckCircle2,
  CalendarClock, Settings2, Lock, Wrench,
} from 'lucide-react';
import { toast } from 'sonner';
import { schedulerApi } from '../services/api';
import { formatRelativo, formatFecha } from '../utils/dateFormat';

const JOB_LABELS = {
  erp_email_reader: { titulo: 'ERP · Lectura de email', icon: '📧', categoria: 'ERP' },
  erp_sftp_reader: { titulo: 'ERP · Lectura SFTP', icon: '📁', categoria: 'ERP' },
  erp_cifs_pendientes_review: { titulo: 'ERP · Revisión CIFs pendientes', icon: '⚠️', categoria: 'ERP' },
  inteligencia_incremental: { titulo: 'Inteligencia municipal incremental', icon: '🏛️', categoria: 'IA' },
  monitor_noticias: { titulo: 'Monitor sectorial · noticias', icon: '📰', categoria: 'IA' },
  alertas_caducidad: { titulo: 'Alertas de caducidad', icon: '🔔', categoria: 'Alertas' },
  sync_ms_graph_all: { titulo: 'Sincronización Microsoft Graph', icon: '📨', categoria: 'Correos' },
  sync_gmail_all: { titulo: 'Sincronización Gmail', icon: '✉️', categoria: 'Correos' },
  actualizar_municipios_ine: { titulo: 'Refresco INE de municipios', icon: '🗺️', categoria: 'Catálogos' },
  recheck_contratos_agua: { titulo: 'Re-check contratos de agua (Perplexity)', icon: '💧', categoria: 'IA' },
  plan_semanal_auto: { titulo: 'Plan Semanal IA · auto', icon: '✨', categoria: 'IA' },
  importar_contratos_agua: {
    titulo: 'Importar contratos de agua (PLACE · Catalunya · Euskadi · Portugal)',
    icon: '📥', categoria: 'Prospección v2',
  },
  detectar_licitaciones_agua: {
    titulo: 'Detectar licitaciones abiertas (CPV 65100000 · 90400000)',
    icon: '🔎', categoria: 'Prospección v2',
  },
  sync_calendarios: {
    titulo: 'Sincronizar calendarios (Outlook + Google) — polling 5 min',
    icon: '📅', categoria: 'Correos & Calendario',
  },
};

const triggerToHumano = (t) => {
  if (!t || typeof t !== 'object') return '—';
  if (t.type === 'interval') {
    if (t.minutes) return `Cada ${t.minutes} min`;
    if (t.hours) return `Cada ${t.hours} h`;
    return 'Intervalo';
  }
  if (t.type === 'cron') {
    const parts = [];
    if (t.day_of_week) parts.push(`Lun-Dom: ${t.day_of_week}`);
    if (t.day) parts.push(`Día ${t.day}`);
    if (t.hour !== undefined) parts.push(`${String(t.hour).padStart(2, '0')}:${String(t.minute || 0).padStart(2, '0')}`);
    return parts.length ? parts.join(' · ') : 'Cron';
  }
  return JSON.stringify(t);
};

const TriggerEditor = ({ trigger, onChange }) => {
  const t = trigger || {};
  const setField = (k, v) => {
    const next = { ...t, [k]: v };
    onChange(next);
  };
  return (
    <div className="space-y-2">
      <select
        value={t.type || 'interval'}
        onChange={(e) => onChange(e.target.value === 'interval'
          ? { type: 'interval', minutes: 60 }
          : { type: 'cron', hour: 8, minute: 0 })}
        className="h-8 w-full rounded border border-ink-200 bg-white text-xs px-2"
        data-testid="trigger-type"
      >
        <option value="interval">Intervalo</option>
        <option value="cron">Hora fija (cron)</option>
      </select>
      {t.type === 'interval' && (
        <div className="flex items-center gap-2">
          <span className="text-xs text-ink-500">Cada</span>
          <Input
            type="number" min={1}
            value={t.minutes ?? 60}
            onChange={(e) => setField('minutes', Number(e.target.value))}
            className="h-8 w-20 text-xs"
          />
          <span className="text-xs text-ink-500">minutos</span>
        </div>
      )}
      {t.type === 'cron' && (
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="text-[10px] text-ink-500 uppercase tracking-wider">Hora</label>
            <Input
              type="number" min={0} max={23}
              value={t.hour ?? 8}
              onChange={(e) => setField('hour', Number(e.target.value))}
              className="h-8 text-xs"
            />
          </div>
          <div>
            <label className="text-[10px] text-ink-500 uppercase tracking-wider">Minuto</label>
            <Input
              type="number" min={0} max={59}
              value={t.minute ?? 0}
              onChange={(e) => setField('minute', Number(e.target.value))}
              className="h-8 text-xs"
            />
          </div>
          <div className="col-span-2">
            <label className="text-[10px] text-ink-500 uppercase tracking-wider">Día semana (opc.)</label>
            <select
              value={t.day_of_week ?? ''}
              onChange={(e) => setField('day_of_week', e.target.value || undefined)}
              className="h-8 w-full rounded border border-ink-200 bg-white text-xs px-2"
            >
              <option value="">Cualquiera</option>
              <option value="mon">Lunes</option>
              <option value="tue">Martes</option>
              <option value="wed">Miércoles</option>
              <option value="thu">Jueves</option>
              <option value="fri">Viernes</option>
              <option value="sat">Sábado</option>
              <option value="sun">Domingo</option>
            </select>
          </div>
        </div>
      )}
    </div>
  );
};

const JobRow = ({ job, onUpdate }) => {
  const meta = JOB_LABELS[job.job_id] || { titulo: job.job_id, icon: '⚙️', categoria: 'Otro' };
  const [editing, setEditing] = useState(false);
  const [draftTrigger, setDraftTrigger] = useState(job.trigger);
  const [busy, setBusy] = useState(false);

  const toggleActivo = async (v) => {
    setBusy(true);
    try {
      await onUpdate({ activo: v, trigger: job.trigger });
    } finally { setBusy(false); }
  };

  const guardarTrigger = async () => {
    setBusy(true);
    try {
      await onUpdate({ activo: job.activo, trigger: draftTrigger });
      setEditing(false);
    } finally { setBusy(false); }
  };

  return (
    <div
      className={`p-4 border rounded-lg transition ${job.activo ? 'border-emerald-200 bg-emerald-50/30' : 'border-ink-200 bg-white'}`}
      data-testid={`scheduler-job-${job.job_id}`}
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <span className="text-lg" aria-hidden>{meta.icon}</span>
            <h3 className="font-semibold text-ink-900 text-sm">{meta.titulo}</h3>
            <Badge className="text-[10px] bg-ink-100 text-ink-600 border-0">{meta.categoria}</Badge>
            {!job.configurable && (
              <span className="inline-flex items-center gap-1 text-[10px] text-amber-700">
                <Lock className="w-3 h-3" /> No configurable
              </span>
            )}
            {job.handler_placeholder && (
              <Badge
                className="text-[10px] bg-amber-100 text-amber-800 border border-amber-300"
                data-testid={`scheduler-placeholder-${job.job_id}`}
                title="Este job aún no tiene implementación real. Activarlo no producirá efecto."
              >
                <Wrench className="w-3 h-3 mr-1 inline" /> No implementado
              </Badge>
            )}
          </div>
          <p className="text-xs text-ink-500 mb-2">{job.descripcion}</p>
          {job.handler_placeholder && (
            <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1 mb-2">
              ⚠ Sin handler real conectado. Pendiente de implementación en sprints siguientes.
            </p>
          )}
          <div className="flex items-center gap-3 flex-wrap text-[11px] text-ink-500">
            <span className="inline-flex items-center gap-1">
              <CalendarClock className="w-3 h-3" /> {triggerToHumano(job.trigger)}
            </span>
            {job.proxima_ejecucion && (
              <span className="inline-flex items-center gap-1 text-emerald-700">
                <Clock className="w-3 h-3" /> Próxima: {formatRelativo(job.proxima_ejecucion)}
              </span>
            )}
            {job.ultima_ejecucion && (
              <span className="text-ink-400">
                Última: {formatFecha(job.ultima_ejecucion)}
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Switch
            checked={job.activo}
            disabled={busy || !job.configurable}
            onCheckedChange={toggleActivo}
            data-testid={`scheduler-toggle-${job.job_id}`}
          />
          <span className={`text-xs font-medium ${job.activo ? 'text-emerald-700' : 'text-ink-500'}`}>
            {job.activo ? 'Activo' : 'Pausado'}
          </span>
          {job.configurable && (
            <Button
              variant="outline" size="sm"
              onClick={() => { setDraftTrigger(job.trigger); setEditing((v) => !v); }}
              data-testid={`scheduler-edit-${job.job_id}`}
            >
              <Settings2 className="w-3.5 h-3.5 mr-1" /> {editing ? 'Cerrar' : 'Configurar'}
            </Button>
          )}
        </div>
      </div>
      {editing && (
        <div className="mt-3 pt-3 border-t border-ink-100 grid grid-cols-1 md:grid-cols-[1fr,auto] gap-3 items-end">
          <TriggerEditor trigger={draftTrigger} onChange={setDraftTrigger} />
          <Button
            onClick={guardarTrigger}
            disabled={busy}
            className="bg-brand-500 hover:bg-brand-600"
            data-testid={`scheduler-save-${job.job_id}`}
          >
            {busy ? 'Guardando…' : 'Guardar'}
          </Button>
        </div>
      )}
    </div>
  );
};

const Scheduler = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const cargar = async () => {
    setLoading(true);
    try {
      const r = await schedulerApi.list();
      setData(r);
    } catch (e) { toast.error(e.message); }
    finally { setLoading(false); }
  };

  useEffect(() => { cargar(); }, []);

  const updateJob = async (jobId, body) => {
    try {
      await schedulerApi.update(jobId, body);
      toast.success(`Job ${jobId}: ${body.activo ? 'activado' : 'pausado/actualizado'}`);
      await cargar();
    } catch (e) { toast.error(e.message); }
  };

  const jobs = data?.jobs || [];
  const activos = jobs.filter((j) => j.activo).length;
  const placeholders = jobs.filter((j) => j.handler_placeholder).length;
  const grouped = jobs.reduce((acc, j) => {
    const cat = (JOB_LABELS[j.job_id]?.categoria) || 'Otro';
    (acc[cat] = acc[cat] || []).push(j);
    return acc;
  }, {});

  return (
    <div data-testid="scheduler-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-heading font-bold text-ink-900 flex items-center gap-2">
            <Clock className="w-7 h-7 text-brand-600" /> Tareas programadas
          </h1>
          <p className="text-ink-500 mt-1">
            Activa, pausa o reprograma los jobs automáticos del CRM. Cambios en caliente, sin reiniciar.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Badge className={data?.running ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-rose-50 text-rose-700 border-rose-200'}>
            {data?.running ? <CheckCircle2 className="w-3 h-3 mr-1 inline" /> : <AlertCircle className="w-3 h-3 mr-1 inline" />}
            Scheduler {data?.running ? 'corriendo' : 'detenido'}
          </Badge>
          <Badge className="bg-brand-50 text-brand-700 border-brand-200">
            {activos} activo{activos === 1 ? '' : 's'} · {jobs.length} total
          </Badge>
          <Button variant="outline" size="sm" onClick={cargar} data-testid="scheduler-reload">
            <RefreshCw className="w-4 h-4 mr-1.5" /> Recargar
          </Button>
        </div>
      </div>

      {!data?.running && (
        <Card className="p-4 mb-6 border-amber-200 bg-amber-50">
          <p className="text-sm text-amber-800 flex items-center gap-2">
            <AlertCircle className="w-4 h-4 shrink-0" />
            El scheduler no está corriendo en este entorno (variable <code className="font-mono text-xs bg-white px-1 rounded">SCHEDULER_ENABLED</code> ≠ <code className="font-mono text-xs bg-white px-1 rounded">true</code>).
            Puedes activarlo en producción cambiando la variable de entorno y reiniciando el backend. Los cambios que hagas aquí se guardan en BD y se aplicarán en cuanto arranque.
          </p>
        </Card>
      )}

      {placeholders > 0 && (
        <Card
          className="p-4 mb-6 border-amber-200 bg-amber-50"
          data-testid="scheduler-placeholders-banner"
        >
          <p className="text-sm text-amber-800 flex items-center gap-2">
            <Wrench className="w-4 h-4 shrink-0" />
            <span>
              <strong>{placeholders}</strong> job{placeholders === 1 ? '' : 's'} sin implementación real (placeholder).
              Marcado{placeholders === 1 ? '' : 's'} en la lista. Activarlos no produce efectos hasta que se conecten a su servicio correspondiente.
            </span>
          </p>
        </Card>
      )}

      {loading && <p className="text-ink-500" data-testid="scheduler-loading">Cargando jobs…</p>}

      {!loading && Object.entries(grouped).map(([cat, lista]) => (
        <section key={cat} className="mb-8">
          <h2 className="text-xs font-semibold text-ink-500 uppercase tracking-wider mb-3">{cat}</h2>
          <div className="grid grid-cols-1 gap-3">
            {lista.map((job) => (
              <JobRow key={job.job_id} job={job} onUpdate={(body) => updateJob(job.job_id, body)} />
            ))}
          </div>
        </section>
      ))}
    </div>
  );
};

export default Scheduler;
